import { useEffect, useState } from 'react'
import { collection, getDocs, updateDoc, doc, serverTimestamp, addDoc, query, where } from 'firebase/firestore'
import { db } from '../../services/firebase'
import { getDoc } from 'firebase/firestore'
import { ArrowLeft, Loader, CheckCircle, XCircle, Clock, DollarSign, AlertCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'

export default function ValidarPagos() {
  const [proofs, setProofs] = useState([])
  const [loading, setLoading] = useState(false)
  const [userInfo, setUserInfo] = useState({})
  const [activeTab, setActiveTab] = useState('pending') // 'pending' o 'processed'

  useEffect(() => {
    fetchProofs()
  }, [])

  const fetchProofs = async () => {
    setLoading(true)
    const snapshot = await getDocs(collection(db, 'paymentProofs'))
    const proofsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    setProofs(proofsData)
    // Fetch user info for each proof
    const userIds = [...new Set(proofsData.map(p => p.userId))]
    const userInfoObj = {}
    for (const uid of userIds) {
      if (!uid) continue
      try {
        const userDoc = await getDocs(collection(db, 'users'))
        const user = userDoc.docs.find(doc => doc.id === uid)
        if (user) {
          const data = user.data()
          userInfoObj[uid] = {
            email: data.email,
            fullName: data.fullName || '',
          }
        }
      } catch (e) {
        userInfoObj[uid] = { email: '', fullName: '' }
      }
    }
    setUserInfo(userInfoObj)
    setLoading(false)
  }

  const handleApprove = async (id) => {
    try {
      setLoading(true)
      // Actualizar estado del depósito
      await updateDoc(doc(db, 'paymentProofs', id), { 
        status: 'approved',
        processedAt: serverTimestamp()
      })
      // Obtener datos del depósito
      const proofDoc = await getDocs(collection(db, 'paymentProofs'))
      const proof = proofDoc.docs.find(doc => doc.id === id)
      if (proof) {
        const { userId, amount } = proof.data()
        // Actualizar saldo del usuario
        const userRef = doc(db, 'users', userId)
        const userSnap = await getDoc(userRef)
        if (userSnap.exists()) {
          const wallet = userSnap.data().wallet || { deposited: 0, winnings: 0, total: 0 }
          const newDeposited = (wallet.deposited || 0) + amount
          const newTotal = (wallet.total || 0) + amount
          await updateDoc(userRef, {
            wallet: {
              ...wallet,
              deposited: newDeposited,
              total: newTotal
            }
          })
          // Crear transacción en colección de nivel superior
          await addDoc(collection(db, 'transactions'), {
            userId: userId,
            type: 'deposit',
            method: 'clabe',
            amount,
            status: 'approved',
            description: 'Depósito por transferencia CLABE',
            createdAt: serverTimestamp(),
          })
        }
      }
      toast.success('Depósito aprobado y saldo actualizado')
      await fetchProofs()
    } catch (error) {
      console.error('Error approving payment:', error)
      toast.error('Error al aprobar el pago')
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async (id, reason) => {
    try {
      setLoading(true)
      await updateDoc(doc(db, 'paymentProofs', id), { 
        status: 'rejected', 
        rejectionReason: reason,
        processedAt: serverTimestamp()
      })
      // Actualizar la transacción en el historial del usuario
      const proofDoc = await getDocs(collection(db, 'paymentProofs'))
      const proof = proofDoc.docs.find(doc => doc.id === id)
      if (proof) {
        const { userId, amount } = proof.data()
        // Buscar transacción pendiente en colección de nivel superior
        const q = query(
          collection(db, 'transactions'),
          where('userId', '==', userId),
          where('amount', '==', amount),
          where('status', '==', 'pending'),
          where('method', '==', 'clabe')
        )
        const txSnapshot = await getDocs(q)
        if (!txSnapshot.empty) {
          await updateDoc(txSnapshot.docs[0].ref, { status: 'rejected', rejectionReason: reason })
        }
      }
      toast.error('Depósito rechazado')
      await fetchProofs()
    } catch (error) {
      console.error('Error rejecting payment:', error)
      toast.error('Error al rechazar el pago')
    } finally {
      setLoading(false)
    }
  }

  // Filtrar movimientos según el tab activo
  const filteredProofs = activeTab === 'pending' 
    ? proofs.filter(p => p.status === 'pending')
    : proofs.filter(p => p.status !== 'pending')

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F8F8F8] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/operator"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F8F8F8] mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Volver al Panel
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#D4AF37] mb-2">
                Gestión de Pagos
              </h1>
              <p className="text-[#BDBDBD]">
                Revisa y procesa los comprobantes de pago por transferencia CLABE
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg px-4 py-2">
                <span className="text-[#BDBDBD]">Pendientes:</span>
                <span className="ml-2 font-bold text-[#F59E0B]">
                  {proofs.filter(p => p.status === 'pending').length}
                </span>
              </div>
              <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg px-4 py-2">
                <span className="text-[#BDBDBD]">Procesados:</span>
                <span className="ml-2 font-bold text-[#22C55E]">
                  {proofs.filter(p => p.status !== 'pending').length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              activeTab === 'pending'
                ? 'bg-[#D4AF37] text-[#1A1A1A]'
                : 'bg-[#222222] text-[#F8F8F8] hover:bg-[#2A2A2A] border border-[#0F3D2E]'
            }`}
          >
            <Clock size={18} />
            Por Procesar
            {proofs.filter(p => p.status === 'pending').length > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'pending' ? 'bg-[#1A1A1A] text-[#D4AF37]' : 'bg-[#F59E0B] text-[#1A1A1A]'
              }`}>
                {proofs.filter(p => p.status === 'pending').length}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('processed')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
              activeTab === 'processed'
                ? 'bg-[#D4AF37] text-[#1A1A1A]'
                : 'bg-[#222222] text-[#F8F8F8] hover:bg-[#2A2A2A] border border-[#0F3D2E]'
            }`}
          >
            <CheckCircle size={18} />
            Procesados
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="animate-spin text-[#D4AF37]" size={48} />
          </div>
        ) : filteredProofs.length === 0 ? (
          <div className="card bg-[#222222] border border-[#0F3D2E] p-12 text-center">
            <AlertCircle className="mx-auto mb-4 text-[#BDBDBD]" size={48} />
            <h3 className="text-xl font-semibold text-[#F8F8F8] mb-2">
              {activeTab === 'pending' ? 'No hay pagos pendientes' : 'No hay pagos procesados'}
            </h3>
            <p className="text-[#BDBDBD]">
              {activeTab === 'pending' 
                ? 'Los comprobantes de pago aparecerán aquí cuando los usuarios los suban.'
                : 'Los pagos que apruebes o rechaces aparecerán en esta sección.'
              }
            </p>
          </div>
        ) : (
          <div className="card bg-[#222222] border border-[#0F3D2E] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-[#F8F8F8]">
                <thead>
                  <tr className="bg-[#0F3D2E]">
                    <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                    <th className="px-4 py-3 text-left font-semibold">Monto</th>
                    <th className="px-4 py-3 text-left font-semibold">Método</th>
                    <th className="px-4 py-3 text-left font-semibold">Referencia</th>
                    <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                    <th className="px-4 py-3 text-left font-semibold">Estado</th>
                    <th className="px-4 py-3 text-left font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0F3D2E]">
                  {filteredProofs.map((proof) => (
                    <tr key={proof.id} className="hover:bg-[#1A1A1A] transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#0F3D2E] rounded-full flex items-center justify-center">
                            <DollarSign size={20} className="text-[#D4AF37]" />
                          </div>
                          <div>
                            <div className="font-medium text-[#F8F8F8]">
                              {userInfo[proof.userId]?.fullName || userInfo[proof.userId]?.username || 'Cargando...'}
                            </div>
                            <div className="text-xs text-[#BDBDBD]">
                              {userInfo[proof.userId]?.email || proof.userId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-lg font-bold text-[#D4AF37]">
                          ${proof.amount?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-[#BDBDBD] capitalize">
                          {proof.method || 'CLABE'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <code className="text-xs bg-[#1A1A1A] px-2 py-1 rounded text-[#D4AF37]">
                          {proof.reference || '-'}
                        </code>
                      </td>
                      <td className="px-4 py-4 text-[#BDBDBD]">
                        {proof.createdAt?.toDate?.()?.toLocaleDateString('es-MX') || '-'}
                      </td>
                      <td className="px-4 py-4">
                        {proof.status === 'pending' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-medium">
                            <Clock size={12} />
                            Pendiente
                          </span>
                        )}
                        {proof.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#22C55E]/20 text-[#22C55E] text-xs font-medium">
                            <CheckCircle size={12} />
                            Aprobado
                          </span>
                        )}
                        {proof.status === 'rejected' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#EF4444]/20 text-[#EF4444] text-xs font-medium">
                            <XCircle size={12} />
                            Rechazado
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {proof.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(proof.id)}
                              className="btn-sm bg-[#0F3D2E] text-[#F8F8F8] hover:bg-[#0A2D1F] flex items-center gap-1"
                              disabled={loading}
                            >
                              <CheckCircle size={14} />
                              Aprobar
                            </button>
                            <button
                              onClick={() => handleReject(proof.id, 'Sin comprobante válido')}
                              className="btn-sm bg-[#B11217] text-[#F8F8F8] hover:bg-[#8B0E12] flex items-center gap-1"
                              disabled={loading}
                            >
                              <XCircle size={14} />
                              Rechazar
                            </button>
                          </div>
                        )}
                        {proof.status === 'rejected' && (
                          <div className="text-xs text-[#EF4444]">
                            Motivo: {proof.rejectionReason}
                          </div>
                        )}
                        {proof.status === 'approved' && (
                          <div className="text-xs text-[#BDBDBD]">
                            {proof.processedAt?.toDate?.()?.toLocaleString('es-MX')}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
